/**
 * Master Configuration
 *
 * Single source of truth for all module configs with TypeScript type safety.
 * Provides autocomplete and type checking for all configuration access.
 */
import configData from './config.json';

// TypeScript interfaces for autocomplete and type safety

export interface AttributeConfig {
  primary: string;
  aliases: string[];
}

export interface ModuleConfig {
  module: string;
  description?: string;
  attributes?: {
    elements?: Record<string, AttributeConfig>;
    properties?: Record<string, string>;
    placeholders?: Record<string, string>;
    note?: string;
  };
  modes?: Record<string, string>;
  modifiers?: Record<string, string>;
  limits?: Record<string, number>;
  types?: Record<string, string>;
  defaults?: Record<string, string>;
}

export interface GlobalConfig {
  version: string;
  apiName: string;
  attributes: {
    height: string;
    heightValue: string;
    heightDuration: string;
    heightEase: string;
    moduleType: string;
    module: string;
  };
  classes: {
    modalOpen: string;
    overflowHidden: string;
    initialized: string;
    displayContents: string;
    active: string;
    gsap: string;
  };
  cssVars: {
    prefix: string;
    state: {
      name: string;
      values: {
        active: string;
        inactive: string;
      };
    };
    clip: {
      name: string;
    };
  };
  clickable: ModuleConfig;
}

export interface Config {
  _global: GlobalConfig;
  normalize?: {
    schema?: ModuleConfig;
    sync?: ModuleConfig;
    clickable?: ModuleConfig & {
      normalize?: ModuleConfig;
    };
    dupe?: ModuleConfig;
    replace?: ModuleConfig;
  };
  default?: {
    transition?: ModuleConfig;
    'smooth-scroll'?: ModuleConfig;
    accordion?: ModuleConfig;
    navbar?: ModuleConfig & {
      dropdown?: ModuleConfig;
      menu?: ModuleConfig;
      'arrow-navigation'?: ModuleConfig;
    };
    toc?: ModuleConfig & {
      'progress-bar'?: ModuleConfig;
    };
    pagination?: ModuleConfig;
    comparison?: ModuleConfig;
    tabs?: ModuleConfig & {
      cssVars?: {
        progress?: string;
      };
    };
    form?: {
      honeypot?: ModuleConfig;
      range?: ModuleConfig & {
        cssVars?: {
          progress?: string;
        };
      };
      test?: ModuleConfig;
      select?: ModuleConfig & {
        states?: ModuleConfig;
        'custom-select'?: ModuleConfig;
      };
    };
  };
  animations?: {};
  [key: string]: unknown;
}

// Export typed config
export const config = configData as Config;
export default config;
